import { useState } from "react";
import { Star, MapPin, Phone, Clock, Navigation } from "lucide-react";
import { business, fullAddress } from "@/config/business";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { buildWhatsAppUrl, contactMessage } from "@/lib/whatsapp";
import { images } from "@/config/site";
import { useSeo } from "@/lib/seo";

const Field = ({ label, as = "input", ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {as === "textarea"
      ? <textarea rows={4} {...props} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-[hsl(var(--ink))]" />
      : <input {...props} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-[hsl(var(--ink))]" />}
  </label>
);

export default function Contact() {
  useSeo({
    title: "Contact Mobile Specialist — Sector 3, Rohini, Delhi",
    description: "Contact Mobile Specialist in Sector 3, Rohini. Call, WhatsApp or get directions. Open every day 11 AM – 9 PM. Rated 4.9/5 on Google.",
    path: "/contact",
  });
  const [f, setF] = useState({ name: "", phone: "", message: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="container-wide py-12" data-testid="contact-page">
      <header className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Contact</p>
        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Get in touch with Mobile Specialist</h1>
      </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="rounded-3xl border border-border bg-card p-8">
            <p className="font-display text-2xl font-bold">Mobile Specialist</p>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 accent-text" /> {fullAddress}</li>
              <li className="flex items-center gap-3"><Phone className="h-5 w-5 accent-text" /> <a href={`tel:${business.phone}`} className="hover:accent-text">{business.phoneDisplay}</a></li>
              <li className="flex items-center gap-3"><Clock className="h-5 w-5 accent-text" /> {business.hoursSummary}</li>
              <li className="flex items-center gap-3"><Star className="h-5 w-5 fill-[hsl(var(--accent))] accent-text" /> {business.googleRating}/5 on Google · {business.googleReviewCount} Reviews</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${business.phone}`} className="btn-ink" data-testid="contact-call"><Phone className="h-4 w-4" /> Call</a>
              <WhatsAppButton label="WhatsApp" testId="contact-whatsapp" />
              <a href={business.googleDirectionsUrl} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="contact-directions"><Navigation className="h-4 w-4" /> Directions</a>
            </div>
          </div>
          <a href={business.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="group mt-4 block overflow-hidden rounded-3xl border border-border" aria-label="Open in Google Maps">
            <img src={images.storeInterior} alt="Mobile Specialist store location, Rohini" className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          </a>
        </div>

        <form className="space-y-4 rounded-3xl border border-border bg-card p-8" onSubmit={(e) => e.preventDefault()} data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold">Send an enquiry</h2>
          <p className="text-sm text-[hsl(var(--ink-soft))]">Your message opens in WhatsApp, ready to send to our team.</p>
          <Field label="Name" placeholder="Your name" value={f.name} onChange={set("name")} data-testid="contact-name" />
          <Field label="Phone" placeholder="Your number" value={f.phone} onChange={set("phone")} data-testid="contact-phone" />
          <Field label="Message" as="textarea" placeholder="How can we help?" value={f.message} onChange={set("message")} data-testid="contact-message" />
          <a href={buildWhatsAppUrl(contactMessage(f))} target="_blank" rel="noopener noreferrer" className="btn-accent w-full" data-testid="contact-submit">Send on WhatsApp</a>
        </form>
      </div>
    </div>
  );
}
